import React, { useCallback, useState, useEffect } from "react";
import {
  Header,
  Footer,
  HeroSection,
  AboutSection,
  EventsSection,
  TeamSection,
  GallerySection,
  RegisterSection,
  EventFlyerModal,
} from "./components";
import { GalleryViewerProvider } from "./components/gallery/GalleryViewerProvider";
import DynamicPage from "./components/pages/DynamicPage";
import { AppRegistrationToaster } from "./components/AppRegistrationToaster";
import MemberRegistrationPage from "./components/pages/MemberRegistrationPage";
import EventRegistrationDrawer from "./components/forms/EventRegistrationDrawer";
import Bulandi2026Page from "./components/pages/Bulandi2026Page";
import { applyDocumentTheme } from "./utils/useThemePreference";
import { canRegisterForEvent, eventFlyerSrc } from "./utils/publicEventsApi";
import { HOME_SECTION_IDS, activeSectionFromEntries } from "./utils/scrollSpy";
import { pageSlugFromPath, usePublicNavPages } from "./utils/publicPagesApi";
const App = () => {
  const path = window.location.pathname;
  const dynamicSlug = pageSlugFromPath(path);
  const isDynamicPage = Boolean(dynamicSlug);
  const isMemberRegistrationPage = path === "/member-registration";
  const isBulandi2026Page = path === "/bulandi-2026";
  const isHomePage = !isMemberRegistrationPage && !isBulandi2026Page && !isDynamicPage;
  const navPages = usePublicNavPages();
  const [activeSection, setActiveSection] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedEventFlyer, setSelectedEventFlyer] = useState(null);
  const [registrationEvent, setRegistrationEvent] = useState(null);
  const closeRegistration = useCallback(() => setRegistrationEvent(null), []);
  useEffect(() => {
    if (!selectedEventFlyer) return undefined;
    const handleEscape = (e) => {
      if (e.key === "Escape") setSelectedEventFlyer(null);
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [selectedEventFlyer]);
  useEffect(() => {
    if (isDynamicPage) return;
    document.title = isMemberRegistrationPage
      ? "Become a member · SMYM Chennai"
      : isBulandi2026Page
        ? "Bulandi 2026 · SMYM Chennai"
        : "SMYM Chennai";
  }, [isMemberRegistrationPage, isBulandi2026Page, isDynamicPage]);
  useEffect(() => {
    if (!isHomePage && !isDynamicPage) return;
    applyDocumentTheme("light");
  }, [isHomePage, isDynamicPage]);
  useEffect(() => {
    if (!isHomePage || typeof IntersectionObserver !== "function") return;
    const nodes = HOME_SECTION_IDS.map((id) => document.getElementById(id)).filter(
      Boolean,
    );
    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.set(entry.target.id, entry);
          else visible.delete(entry.target.id);
        }
        const atPageEnd =
          window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 4;
        const next = activeSectionFromEntries([...visible.values()], { atPageEnd });
        if (next) setActiveSection(next);
      },
      { rootMargin: "-64px 0px -15% 0px", threshold: [0, 0.1, 0.25, 0.6] },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [isHomePage]);
  const scrollToSection = (section) => {
    setActiveSection(section);
    setMobileMenuOpen(false);
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };
  if (isMemberRegistrationPage) {
    return (
      <>
        <AppRegistrationToaster />
        <MemberRegistrationPage />
      </>
    );
  }
  if (isBulandi2026Page) {
    return (
      <>
        <AppRegistrationToaster />
        <Bulandi2026Page />
      </>
    );
  }
  return (
    <>
      <AppRegistrationToaster />
      <div className="smym-home relative min-h-screen">
        <GalleryViewerProvider>
          <a href="#home" className="smym-skip">
            Skip to content
          </a>
          <Header
            activeSection={activeSection}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            scrollToSection={scrollToSection}
            navPages={navPages}
          />

          {isDynamicPage ? (
            <DynamicPage slug={dynamicSlug} />
          ) : (
            <>
              <HeroSection />

              <AboutSection />

              <EventsSection
                onViewFlyer={(event) => {
                  if (eventFlyerSrc(event?.flyer)) setSelectedEventFlyer(event);
                }}
                onRegisterEvent={(event) => {
                  if (canRegisterForEvent(event)) setRegistrationEvent(event);
                }}
              />

              <TeamSection />

              <GallerySection />

              <RegisterSection />
            </>
          )}

          <Footer navPages={navPages} />

          <EventFlyerModal
            event={selectedEventFlyer}
            onClose={() => setSelectedEventFlyer(null)}
            onRegister={(event) => {
              if (canRegisterForEvent(event)) setRegistrationEvent(event);
            }}
          />

          <EventRegistrationDrawer
            event={registrationEvent}
            onClose={closeRegistration}
          />
        </GalleryViewerProvider>
      </div>
    </>
  );
};
export default App;
